import { reactive, ref, type Ref } from 'vue';
import { Vector2 } from 'three';
import type { LumbarLevel } from '@/domain/anatomy/hipSpineLandmarks';
import type { SideId } from '@/domain/anatomy/side';
import type { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';

export type EditedLandmark = { current_x: number; current_y: number };

export type PostureEdits = {
    landmarks: Record<string, EditedLandmark>;
    femurRadius: { 1?: number; 2?: number };
};

export type DragState =
    | { kind: 'idle' }
    | {
          kind: 'landmark';
          postureId: string;
          landmarkName: string;
          landmarkStartWorld: Vector2;
          pointerStartWorld: Vector2;
      }
    | {
          kind: 'femur-resize';
          postureId: string;
          head: 1 | 2;
          radiusStart: number;
          pointerStartDistance: number;
          pointerWorld: Vector2;
      };

export type HoverState =
    | { kind: 'none' }
    | { kind: 'landmark'; landmarkName: string }
    | { kind: 'circle-edge'; head: 1 | 2 };

export type LoupeState = { active: boolean };

// Fallback used only when the API returns a null `posture.femur_*_head_radius`.
// Stored radii are normalized as a fraction of image width (the AI computes them that way),
// so this fallback is too. It rarely kicks in — the radius is populated by the AI / backfill.
export const DEFAULT_FEMUR_HEAD_RADIUS_FALLBACK = 0.09;

/**
 * The per-posture edit state the resolution/action helpers operate on. Created
 * once in the ViewModel setup and bundled (with the Model `store` handle) into
 * an {@link EditContext}, so the logic can move to module-level functions that
 * stay within the function-length cap and remain unit-testable without mounting.
 */
export type EditState = {
    editsByPosture: Map<string, PostureEdits>;
    lumbarLevelByPosture: Map<string, LumbarLevel>;
    viewedSideByPosture: Map<string, SideId>;
    // Visibility intent lives here, not on the scene nodes: a geometry rebuild
    // discards the old group nodes (fresh ones default to visible), so storing
    // intent in the ViewModel is what lets a hidden group survive a rebuild.
    hiddenGroupKeysByPosture: Map<string, Set<string>>;
    dragState: Ref<DragState>;
    hover: Ref<HoverState>;
    loupe: Ref<LoupeState>;
};

export type EditContext = EditState & {
    store: ReturnType<typeof useHipSpineStore>;
};

export function createEditState(): EditState {
    return {
        editsByPosture: reactive(new Map<string, PostureEdits>()),
        lumbarLevelByPosture: reactive(new Map<string, LumbarLevel>()),
        viewedSideByPosture: reactive(new Map<string, SideId>()),
        hiddenGroupKeysByPosture: reactive(new Map<string, Set<string>>()),
        dragState: ref<DragState>({ kind: 'idle' }),
        hover: ref<HoverState>({ kind: 'none' }),
        loupe: ref<LoupeState>({ active: false }),
    };
}
